import React from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import Drawer from '@material-ui/core/Drawer'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import Avatar from '@material-ui/core/Avatar'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import CloseIcon from '@material-ui/icons/Close'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import { makeStyles } from '@material-ui/core/styles'
import { coachColorAt } from './academy-resource-calendar'
import { COACH_SETTINGS_QUERY } from './academy-schedule-queries'

const DAYS_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

const DOW_INDEX = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
}

function formatTimeSlot(slot) {
  if (!slot) return ''
  const formatT = t => {
    if (!t) return ''
    const [h, m] = t.split(':').map(Number)
    const ampm = h >= 12 ? 'pm' : 'am'
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    return m === 0
      ? `${h12}${ampm}`
      : `${h12}:${String(m).padStart(2, '0')}${ampm}`
  }
  return `${formatT(slot.start_time || slot.startTime)} – ${formatT(
    slot.end_time || slot.endTime,
  )}`
}

const useStyles = makeStyles(theme => ({
  drawer: {
    width: 340,
    maxWidth: '90vw',
  },
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 2, 1.5),
    gap: theme.spacing(1.5),
  },
  avatar: {
    width: 44,
    height: 44,
    fontSize: 16,
    flexShrink: 0,
  },
  section: {
    padding: theme.spacing(2, 2, 1),
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1),
  },
  sectionIcon: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  facilityBlock: {
    marginBottom: theme.spacing(1.5),
  },
  facilityName: {
    fontWeight: 600,
    fontSize: 12,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  dayRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  dayLabel: {
    fontSize: 12,
    fontWeight: 500,
    minWidth: 36,
    color: theme.palette.text.primary,
  },
  slotChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  slotChip: {
    height: 20,
    fontSize: 10,
    background: theme.palette.grey[100],
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 0),
  },
  metaLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  metaValue: {
    fontSize: 12,
    fontWeight: 600,
  },
  noAvailability: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  readOnlyNote: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    padding: theme.spacing(0, 2, 2),
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}))

/**
 * Read-only slide-out drawer showing a coach's scheduling settings.
 * Settings data is fetched lazily when the panel opens to keep the
 * main academy calendar query lightweight.
 */
function AcademyCoachSettingsPanel({ open, coach, coachIndex, onClose }) {
  const classes = useStyles()

  const { data, loading } = useQuery(COACH_SETTINGS_QUERY, {
    variables: { coachId: coach?.id },
    skip: !open || !coach?.id,
  })

  if (!coach) return null

  const color = coachColorAt(coachIndex || 0)
  const initials = (coach.name || 'U')
    .split(' ')
    .slice(0, 2)
    .map(s => s[0])
    .join('')
    .toUpperCase()

  const settings = data?.coach || {}
  const bookingLimit = settings.bookingLimit
  const bookingCutoff = settings.bookingCutoff
  const prefs = settings.preferences || {}
  const facilities = settings.facilities || []

  const bookingLimitLabel =
    bookingLimit == null
      ? 'No limit'
      : bookingLimit === 0
        ? 'No advance limit'
        : `${bookingLimit} day${bookingLimit === 1 ? '' : 's'} in advance`

  const cutoffLabel =
    bookingCutoff == null
      ? 'No cutoff'
      : bookingCutoff === 0
        ? 'No cutoff'
        : bookingCutoff < 24
          ? `${bookingCutoff} hour${bookingCutoff === 1 ? '' : 's'} before`
          : `${bookingCutoff / 24} day${
            bookingCutoff / 24 === 1 ? '' : 's'
          } before`

  const cancelLabel = prefs.canStudentCancel ? 'Allowed' : 'Not allowed'

  const facilitiesWithAvailability = facilities.filter(
    f => f.availabilities && f.availabilities.length > 0,
  )

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.drawer }}
    >
      {/* Header — always available from the coach prop */}
      <Box className={classes.titleBar}>
        <Avatar
          className={classes.avatar}
          src={coach.coachProfile?.profilePhoto || undefined}
          style={{ background: color.bg, color: color.text }}
        >
          {!coach.coachProfile?.profilePhoto && initials}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 600, lineHeight: 1.2 }}
          >
            {coach.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Schedule settings
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close settings panel"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      {loading ? (
        <Box className={classes.loadingWrap}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {/* Booking rules */}
          <Box className={classes.section}>
            <Box className={classes.sectionTitle}>
              <CalendarTodayIcon className={classes.sectionIcon} />
              <Typography variant="subtitle2">Booking Rules</Typography>
            </Box>
            <Box className={classes.metaRow}>
              <Typography className={classes.metaLabel}>
                Booking limit
              </Typography>
              <Typography className={classes.metaValue}>
                {bookingLimitLabel}
              </Typography>
            </Box>
            <Divider light />
            <Box className={classes.metaRow}>
              <Typography className={classes.metaLabel}>
                Booking cutoff
              </Typography>
              <Typography className={classes.metaValue}>
                {cutoffLabel}
              </Typography>
            </Box>
            <Divider light />
            <Box className={classes.metaRow}>
              <Typography className={classes.metaLabel}>
                Student cancellations
              </Typography>
              <Typography className={classes.metaValue}>
                {cancelLabel}
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Availability windows */}
          <Box className={classes.section}>
            <Box className={classes.sectionTitle}>
              <AccessTimeIcon className={classes.sectionIcon} />
              <Typography variant="subtitle2">Availability</Typography>
            </Box>

            {facilitiesWithAvailability.length === 0 && (
              <Typography className={classes.noAvailability}>
                No availability set for any facility.
              </Typography>
            )}

            {facilitiesWithAvailability.map(facility => {
              const byDay = {}
              for (const avail of facility.availabilities) {
                const rawSlots = avail.timeSlots
                let slots = rawSlots
                if (typeof rawSlots === 'string') {
                  try {
                    slots = JSON.parse(rawSlots)
                  } catch {
                    slots = []
                  }
                }
                for (const slot of slots || []) {
                  const day = slot.day || slot.day_of_week
                  if (!day) continue
                  if (!byDay[day]) byDay[day] = []
                  byDay[day].push(slot)
                }
              }

              const sortedDays = Object.keys(byDay).sort(
                (a, b) => (DOW_INDEX[a] ?? 7) - (DOW_INDEX[b] ?? 7),
              )

              return (
                <Box key={facility.id} className={classes.facilityBlock}>
                  <Typography className={classes.facilityName}>
                    {facility.name}
                  </Typography>
                  {sortedDays.map(day => {
                    const dayIdx = DOW_INDEX[day]
                    const dayShort = DAYS_FULL[dayIdx] || day
                    return (
                      <Box key={day} className={classes.dayRow}>
                        <Typography className={classes.dayLabel}>
                          {dayShort}
                        </Typography>
                        <Box className={classes.slotChips}>
                          {byDay[day].map((slot, i) => (
                            <Chip
                              key={i}
                              label={formatTimeSlot(slot)}
                              className={classes.slotChip}
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    )
                  })}
                  {sortedDays.length === 0 && (
                    <Typography className={classes.noAvailability}>
                      No availability set.
                    </Typography>
                  )}
                </Box>
              )
            })}
          </Box>
        </>
      )}

      <Typography className={classes.readOnlyNote}>
        Settings are managed by each coach in their PGA Coach settings.
      </Typography>
    </Drawer>
  )
}

AcademyCoachSettingsPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  coach: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    coachProfile: PropTypes.shape({ profilePhoto: PropTypes.string }),
  }),
  coachIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired,
}

export default AcademyCoachSettingsPanel
